import { useCallback, useEffect, useRef, useState } from 'react'

interface Recognition {
  lang: string
  interimResults: boolean
  continuous: boolean
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null
  onend: (() => void) | null
  onerror: ((event: { error: string }) => void) | null
  start: () => void
  abort: () => void
}
interface SpeechWindow extends Window {
  SpeechRecognition?: new () => Recognition
  webkitSpeechRecognition?: new () => Recognition
}

interface DictationResult {
  supported: boolean
  isListening: boolean
  start: () => void
  stop: () => void
}

export function useBrowserDictation (onTranscript: (text: string) => void, onError: (message: string) => void): DictationResult {
  const [isListening, setIsListening] = useState(false)
  const recognitionRef = useRef<Recognition | null>(null)
  const callbacks = useRef({ onTranscript, onError })
  callbacks.current = { onTranscript, onError }
  const speechWindow = window as SpeechWindow
  const Constructor = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition
  const supported = Boolean(Constructor)

  const stop = useCallback(() => {
    recognitionRef.current?.abort()
    recognitionRef.current = null
    setIsListening(false)
  }, [])

  const start = useCallback(() => {
    if (!Constructor || recognitionRef.current) return
    const recognition = new Constructor()
    recognition.lang = 'pt-BR'
    recognition.interimResults = false
    recognition.continuous = false
    recognition.onresult = event => {
      callbacks.current.onTranscript(Array.from(event.results).map(result => result[0].transcript).join(' '))
    }
    recognition.onend = () => { recognitionRef.current = null; setIsListening(false) }
    recognition.onerror = event => {
      if (event.error !== 'aborted') callbacks.current.onError('Não foi possível ditar. Verifique a permissão do microfone.')
    }
    recognitionRef.current = recognition
    try {
      recognition.start()
      setIsListening(true)
    } catch {
      recognitionRef.current = null
      callbacks.current.onError('Não foi possível iniciar o microfone.')
    }
  }, [Constructor])

  useEffect(() => () => { recognitionRef.current?.abort() }, [])
  return { supported, isListening, start, stop }
}
